// src/ProfilePage.jsx
import React, { useState } from 'react';
import { useSubscription } from './hooks/useSubscription';
import { updateProfile, updatePassword } from './services/authService';

function ProfilePage({ user, onBack, onLogout }) {
  const { 
    profile,
    // role,  // Non utilisé directement, on utilise isGratuit, isNormal, etc.
    isGratuit,
    isNormal,
    isExpert,
    isAdmin,
    matchesCount,
    matchesLimit,
    matchesRemaining,
    subscriptionStatus,
    trialDaysRemaining,
    loading 
  } = useSubscription();

  const [editMode, setEditMode] = useState(false);
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // 🆕 Changement de mot de passe
  const [passwordMode, setPasswordMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');

  // 🆕 Préférences
  const [darkMode, setDarkMode] = useState(false);
  const [language, setLanguage] = useState('fr');

  // Initialiser les champs quand le profil charge
  React.useEffect(() => {
    if (profile) {
      setPrenom(profile.prenom || '');
      setNom(profile.nom || '');
    }
  }, [profile]);

  // 🆕 Charger les préférences
  React.useEffect(() => {
    const savedMode = localStorage.getItem('darkMode') === 'true';
    const savedLang = localStorage.getItem('language') || 'fr';
    
    setDarkMode(savedMode);
    setLanguage(savedLang);
  }, []);

  const handleSaveProfile = async () => {
    setSaving(true);
    setMessage('');

    const result = await updateProfile(user.id, { prenom, nom });

    if (result.success) {
      setMessage('✅ Profil mis à jour');
      setEditMode(false);
    } else {
      setMessage(`❌ ${result.error}`);
    }

    setSaving(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleCancelEdit = () => {
    setPrenom(profile.prenom || '');
    setNom(profile.nom || '');
    setEditMode(false);
  };

  // 🆕 Toggle dark mode
  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode);
    document.body.classList.toggle('dark-mode');
  };

  // 🆕 Toggle langue
  const toggleLanguage = () => {
    const newLang = language === 'fr' ? 'en' : 'fr';
    setLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  // 🆕 Changer le mot de passe
  const handleChangePassword = async () => {
    setPasswordMessage('');

    // Validation
    if (!newPassword || newPassword.length < 6) {
      setPasswordMessage('❌ Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage('❌ Les mots de passe ne correspondent pas');
      return;
    }

    setSaving(true);

    const result = await updatePassword(newPassword);

    if (result.success) {
      setPasswordMessage('✅ Mot de passe modifié avec succès');
      setPasswordMode(false);
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setPasswordMessage(`❌ ${result.error}`);
    }

    setSaving(false);
    setTimeout(() => setPasswordMessage(''), 3000);
  };

  const handleCancelPassword = () => {
    setPasswordMode(false);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordMessage('');
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        Chargement du profil...
      </div>
    );
  }

  // Badge selon le rôle
  const getRoleBadge = () => {
    if (isAdmin) return '👑 Administrateur';
    if (isExpert) return '⭐ Expert';
    if (isNormal) return '🎾 Normal';
    return '🆓 Gratuit';
  };

  const getRoleColor = () => {
    if (isAdmin) return '#ff9800';
    if (isExpert) return '#9c27b0';
    if (isNormal) return '#2196f3';
    return '#757575';
  };

  const getStatusBadge = () => {
    if (subscriptionStatus === 'trial') {
      return `🎁 Essai (${trialDaysRemaining}j restants)`;
    }
    if (subscriptionStatus === 'active') {
      return '✅ Actif';
    }
    if (subscriptionStatus === 'expired') {
      return '⏰ Expiré';
    }
    return '⚪ Inactif';
  };

  return (
    <div style={{
      padding: '24px',
      maxWidth: '600px',
      margin: '0 auto',
      fontFamily: 'Arial, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '32px'
      }}>
        <button
          onClick={onBack}
          style={{
            padding: '8px 12px',
            border: 'none',
            background: '#f5f5f5',
            borderRadius: '8px',
            cursor: 'pointer',
            marginRight: '16px'
          }}
        >
          ← Retour
        </button>
        <h1 style={{ margin: 0, fontSize: '24px' }}>Mon Profil</h1>
      </div>

      {/* Message */}
      {message && (
        <div style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: message.includes('✅') ? '#e8f5e9' : '#ffebee',
          borderRadius: '8px',
          color: message.includes('✅') ? '#2e7d32' : '#c62828'
        }}>
          {message}
        </div>
      )}

      {/* Carte Informations personnelles */}
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '16px'
      }}>
        <h2 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '18px',
          color: '#333'
        }}>
          👤 Informations personnelles
        </h2>

        {!editMode ? (
          <>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                Prénom
              </div>
              <div style={{ fontSize: '16px', color: '#333' }}>
                {profile?.prenom || '—'}
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                Nom
              </div>
              <div style={{ fontSize: '16px', color: '#333' }}>
                {profile?.nom || '—'}
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                Email
              </div>
              <div style={{ fontSize: '16px', color: '#333' }}>
                {profile?.email}
              </div>
            </div>

            <button
              onClick={() => setEditMode(true)}
              style={{
                marginTop: '16px',
                padding: '10px 16px',
                backgroundColor: '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ✏️ Modifier mes informations
            </button>
          </>
        ) : (
          <>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ 
                display: 'block',
                fontSize: '12px', 
                color: '#666', 
                marginBottom: '4px' 
              }}>
                Prénom
              </label>
              <input
                type="text"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block',
                fontSize: '12px', 
                color: '#666', 
                marginBottom: '4px' 
              }}>
                Nom
              </label>
              <input
                type="text"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleSaveProfile}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: saving ? '#ccc' : '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {saving ? 'Enregistrement...' : '✅ Enregistrer'}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                ❌ Annuler
              </button>
            </div>
          </>
        )}
      </div>

      {/* Carte Abonnement */}
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '16px'
      }}>
        <h2 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '18px',
          color: '#333'
        }}>
          🎾 Abonnement
        </h2>

        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
            Type de compte
          </div>
          <div style={{ 
            fontSize: '16px', 
            color: getRoleColor(),
            fontWeight: 'bold'
          }}>
            {getRoleBadge()}
          </div>
        </div>

        {isGratuit && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              Matchs créés
            </div>
            <div style={{ fontSize: '16px', color: '#333' }}>
              {matchesCount} / {matchesLimit} matchs
              <span style={{ 
                marginLeft: '8px',
                fontSize: '14px',
                color: matchesRemaining <= 2 ? '#f44336' : '#4caf50'
              }}>
                ({matchesRemaining} restants)
              </span>
            </div>
          </div>
        )}

        {!isGratuit && !isAdmin && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              Statut
            </div>
            <div style={{ fontSize: '16px', color: '#333' }}>
              {getStatusBadge()}
            </div>
          </div>
        )}

        {isGratuit && matchesRemaining <= 2 && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#fff3e0',
            borderRadius: '8px',
            border: '1px solid #ff9800'
          }}>
            <div style={{ fontSize: '14px', color: '#e65100', marginBottom: '8px' }}>
              ⚠️ Vous approchez de la limite !
            </div>
            <button
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#ff9800',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              🚀 Passer à NORMAL (matchs illimités)
            </button>
          </div>
        )}
      </div>

      {/* Carte Préférences */}
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '16px'
      }}>
        <h2 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '18px',
          color: '#333'
        }}>
          🎨 Préférences
        </h2>

        {/* Dark Mode */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px'
        }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
              {darkMode ? '🌙' : '☀️'} Mode {darkMode ? 'sombre' : 'clair'}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              Apparence de l'interface
            </div>
          </div>
          <button
            onClick={toggleDarkMode}
            style={{
              padding: '8px 16px',
              backgroundColor: darkMode ? '#424242' : '#fff',
              color: darkMode ? '#fff' : '#333',
              border: '2px solid #ddd',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {darkMode ? 'Clair' : 'Sombre'}
          </button>
        </div>

        {/* Langue */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px'
        }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
              🌍 Langue
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {language === 'fr' ? 'Français' : 'English'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => { if (language !== 'fr') toggleLanguage(); }}
              style={{
                padding: '8px 16px',
                backgroundColor: language === 'fr' ? '#2196f3' : '#fff',
                color: language === 'fr' ? '#fff' : '#333',
                border: '2px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: language === 'fr' ? 'bold' : 'normal'
              }}
            >
              FR
            </button>
            <button
              onClick={() => { if (language !== 'en') toggleLanguage(); }}
              style={{
                padding: '8px 16px',
                backgroundColor: language === 'en' ? '#2196f3' : '#fff',
                color: language === 'en' ? '#fff' : '#333',
                border: '2px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: language === 'en' ? 'bold' : 'normal'
              }}
            >
              EN
            </button>
          </div>
        </div>
      </div>

      {/* Carte Sécurité */}
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '16px'
      }}>
        <h2 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '18px',
          color: '#333'
        }}>
          🔒 Sécurité
        </h2>

        {/* Message mot de passe */}
        {passwordMessage && (
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: passwordMessage.includes('✅') ? '#e8f5e9' : '#ffebee',
            borderRadius: '8px',
            color: passwordMessage.includes('✅') ? '#2e7d32' : '#c62828',
            fontSize: '14px'
          }}>
            {passwordMessage}
          </div>
        )}

        {!passwordMode ? (
          <button
            onClick={() => setPasswordMode(true)}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ddd',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              textAlign: 'left'
            }}
          >
            🔑 Changer mon mot de passe
          </button>
        ) : (
          <>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ 
                display: 'block',
                fontSize: '12px', 
                color: '#666', 
                marginBottom: '4px' 
              }}>
                Nouveau mot de passe
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Au moins 6 caractères"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block',
                fontSize: '12px', 
                color: '#666', 
                marginBottom: '4px' 
              }}>
                Confirmer le mot de passe
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Retapez le mot de passe"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleChangePassword}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: saving ? '#ccc' : '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {saving ? 'Modification...' : '✅ Confirmer'}
              </button>
              <button
                onClick={handleCancelPassword}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                ❌ Annuler
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ProfilePage;